/**
 * Location health report.
 * Route: /reports/location-health
 * Renders the report. Row math lives in logic/deriveLocationScheduleHealth.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  HoverCard,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { fetchAllLinks, fetchAllTasks, type LocationTreeNode } from '../../api/schedulingApis';
import { usePortalProject } from '../../contexts/PortalProjectContext';
import {
  deleteDashboardInFlight,
  deleteDashboardMemoryCache,
  getDashboardInFlight,
  getDashboardMemoryCache,
  setDashboardInFlight,
  setDashboardMemoryCache,
  type DashboardData,
} from '../../dashboard/dashboardProjectCache';
import { loadTasksFromDB } from '../../stores/db';
import { useLocationsTreeStore } from '../../stores/locationsTreeStore';
import { parseDateSafe } from '../../utils/utils';
import { prepareDashboardData } from '../dashboard/logic/prepareDashboardData';
import {
  deriveLocationScheduleHealth,
  type LocationHealthRow,
} from './logic/deriveLocationScheduleHealth';

const CARD_RADIUS = 'sm';
/** Stable empty tree — avoid `?? []` in Zustand selectors (infinite rerenders). */
const EMPTY_LOCATION_TREE: LocationTreeNode[] = [];

function formatDate(value: string | null | undefined): string {
  const d = parseDateSafe(value ?? null);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function KpiInfoCard({
  title,
  value,
  subtitle,
  info,
}: {
  title: string;
  value: React.ReactNode;
  subtitle: string;
  info: string;
}) {
  return (
    <Card withBorder radius={CARD_RADIUS} p="md" style={{ position: 'relative' }}>
      <HoverCard width={280} shadow="md" withArrow position="bottom-end" openDelay={150} closeDelay={80}>
        <HoverCard.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={`About ${title}`}
            style={{ position: 'absolute', top: 8, right: 8 }}
          >
            <IconInfoCircle size={16} stroke={1.5} />
          </ActionIcon>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Text size="sm">{info}</Text>
        </HoverCard.Dropdown>
      </HoverCard>
      <Text size="xs" c="dimmed" pr={28}>
        {title}
      </Text>
      <Title order={3}>{value}</Title>
      <Text size="xs" c="dimmed">
        {subtitle}
      </Text>
    </Card>
  );
}

function LocationScheduleHealthPage() {
  const { projectId, projectName } = usePortalProject();
  const projectLabel = projectName || projectId || 'your project';
  const fetchTreeIfNeeded = useLocationsTreeStore((s) => s.fetchTreeIfNeeded);
  const locationTree = useLocationsTreeStore((s) =>
    projectId ? (s.treeByProjectId[projectId] ?? EMPTY_LOCATION_TREE) : EMPTY_LOCATION_TREE,
  );

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData>({ tasks: [], links: [] });
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const hasDataRef = React.useRef(false);

  useEffect(() => {
    hasDataRef.current = data.tasks.length > 0;
  }, [data.tasks.length]);

  const fetchFromApiDeduped = useCallback(async (): Promise<DashboardData> => {
    const existing = getDashboardInFlight(projectId);
    if (existing) return existing;
    const requestPromise = (async () => {
      const [tasksResponse, linksResponse] = await Promise.all([
        fetchAllTasks({ projectId }),
        fetchAllLinks({ projectId }).catch(() => ({ data: [] })),
      ]);
      const tasks = Array.isArray(tasksResponse?.data) ? tasksResponse.data : [];
      const links = Array.isArray(linksResponse?.data) ? linksResponse.data : [];
      const result = prepareDashboardData(tasks, links);
      setDashboardMemoryCache(projectId, result);
      return result;
    })();
    setDashboardInFlight(projectId, requestPromise);
    try {
      return await requestPromise;
    } finally {
      deleteDashboardInFlight(projectId);
    }
  }, [projectId]);

  const load = useCallback(
    async (opts?: { forceRefresh?: boolean }) => {
      const forceRefresh = !!opts?.forceRefresh;
      if (!projectId) {
        setData({ tasks: [], links: [] });
        setLoading(false);
        return;
      }

      if (!hasDataRef.current) setLoading(true);
      else if (forceRefresh) setRefreshing(true);
      setError(null);

      try {
        if (forceRefresh) {
          deleteDashboardMemoryCache(projectId);
        }

        const treePromise = fetchTreeIfNeeded(projectId);

        if (!forceRefresh) {
          const inMemory = getDashboardMemoryCache(projectId);
          if (inMemory && inMemory.tasks.length > 0) {
            setData(prepareDashboardData(inMemory.tasks, inMemory.links));
            await treePromise;
            return;
          }
          const fromDb = await loadTasksFromDB(projectId);
          if (fromDb?.tasks?.length) {
            const dbData = prepareDashboardData(fromDb.tasks, fromDb.links ?? []);
            setDashboardMemoryCache(projectId, dbData);
            setData(dbData);
            await treePromise;
            return;
          }
        }

        const [apiData] = await Promise.all([fetchFromApiDeduped(), treePromise]);
        setData(apiData);
      } catch {
        setError('Could not load location health right now.');
        setData((prev) => (prev.tasks.length > 0 ? prev : { tasks: [], links: [] }));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [projectId, fetchFromApiDeduped, fetchTreeIfNeeded],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const report = useMemo(
    () => deriveLocationScheduleHealth(data.tasks, locationTree),
    [data.tasks, locationTree],
  );

  useEffect(() => {
    if (
      selectedLocationId != null &&
      !report.rows.some((r) => r.locationId === selectedLocationId)
    ) {
      setSelectedLocationId(null);
    }
  }, [report.rows, selectedLocationId]);

  const selectedRow: LocationHealthRow | null = useMemo(
    () => report.rows.find((r) => r.locationId === selectedLocationId) ?? null,
    [report.rows, selectedLocationId],
  );

  const toggleRow = (locationId: string) => {
    setSelectedLocationId((prev) => (prev === locationId ? null : locationId));
  };

  const showSkeleton = loading && data.tasks.length === 0;

  return (
    <Box style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      <Box py="xl" px="md" style={{ width: '100%' }}>
        <Stack gap="xl">
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
            <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
              <Title order={2}>Location health</Title>
              <Text c="dimmed" size="sm">
                Progress, overdue work, and open issues by location for {projectLabel}.
              </Text>
            </Stack>
            <Button
              variant="default"
              onClick={() => void load({ forceRefresh: true })}
              loading={loading || refreshing}
              styles={{
                root: {
                  backgroundColor: 'var(--mantine-color-body)',
                  color: 'var(--mantine-color-text)',
                  border: '1px solid var(--mantine-color-default-border)',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: 'var(--mantine-color-default-hover)',
                  },
                },
              }}
            >
              Refresh
            </Button>
          </Group>

          {error ? (
            <Paper radius={CARD_RADIUS} p="md" withBorder>
              <Text c="red">{error}</Text>
            </Paper>
          ) : null}

          {showSkeleton ? (
            <>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <Card key={`kpi-sk-${idx}`} withBorder radius={CARD_RADIUS} p="md">
                    <Skeleton height={10} width="50%" mb="md" />
                    <Skeleton height={28} width="40%" />
                  </Card>
                ))}
              </SimpleGrid>
              <Card withBorder radius={CARD_RADIUS} p="md">
                <Skeleton height={200} />
              </Card>
            </>
          ) : (
            <>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                <KpiInfoCard
                  title="Location coverage"
                  value={`${report.kpis.assignmentCoveragePercent}%`}
                  subtitle={`${report.kpis.assignedTaskCount} of ${report.kpis.leafTaskCount} leaf tasks`}
                  info="Share of leaf tasks that have a location assigned (locationId set). This measures tagging coverage, not how complete work is at each location."
                />
                <KpiInfoCard
                  title="Unassigned tasks"
                  value={report.kpis.unassignedTaskCount}
                  subtitle="with no locations"
                  info="Count of leaf tasks with no locations. These appear in the Unassigned row in the table below."
                />
                <KpiInfoCard
                  title="Locations with overdue"
                  value={report.kpis.locationsWithOverdue}
                  subtitle="rolled-up leaf work"
                  info="Number of locations (excluding Unassigned) that have at least one rolled-up leaf task past its finish date and still under 100% complete. Parent locations include tasks on descendant locations."
                />
                <KpiInfoCard
                  title="Locations with open issues"
                  value={report.kpis.locationsWithOpenIssues}
                  subtitle="linked issues on tasks"
                  info="Number of locations (excluding Unassigned) that have at least one open linked issue on rolled-up leaf tasks. Uses openIssueCount / open linkedIssues on those tasks."
                />
              </SimpleGrid>

              <Box
                className="location-health-split"
                style={{
                  display: 'grid',
                  gridTemplateColumns: selectedRow ? 'minmax(0, 1.6fr) minmax(280px, 1fr)' : '1fr',
                  gap: 16,
                  alignItems: 'stretch',
                  minHeight: 420,
                }}
              >
                <Card
                  withBorder
                  radius={CARD_RADIUS}
                  p="md"
                  style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}
                >
                  <Group justify="space-between" mb="sm" style={{ flexShrink: 0 }}>
                    <Title order={4}>Locations</Title>
                    <Badge variant="light">{report.rows.length}</Badge>
                  </Group>
                  {report.rows.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      No leaf tasks found for this project.
                    </Text>
                  ) : (
                    <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                      <Table.ScrollContainer minWidth={selectedRow ? 520 : 640}>
                        <Table highlightOnHover verticalSpacing="sm">
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Location</Table.Th>
                              <Table.Th>Type</Table.Th>
                              <Table.Th ta="right">Tasks</Table.Th>
                              <Table.Th ta="right">Avg %</Table.Th>
                              <Table.Th ta="right">Overdue</Table.Th>
                              <Table.Th ta="right">Open issues</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {report.rows.map((row) => {
                              const active = selectedLocationId === row.locationId;
                              return (
                                <Table.Tr
                                  key={row.locationId}
                                  style={{
                                    cursor: 'pointer',
                                    background: active ? 'var(--mantine-color-default-hover)' : undefined,
                                  }}
                                  onClick={() => toggleRow(row.locationId)}
                                >
                                  <Table.Td>
                                    <Text size="sm" fw={500}>
                                      {row.name}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size="sm" c="dimmed">
                                      {row.locationType}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td ta="right">{row.taskCount}</Table.Td>
                                  <Table.Td ta="right">{row.avgProgress}%</Table.Td>
                                  <Table.Td ta="right">
                                    {row.overdueCount > 0 ? (
                                      <Badge size="sm" color="red" variant="light">
                                        {row.overdueCount}
                                      </Badge>
                                    ) : (
                                      0
                                    )}
                                  </Table.Td>
                                  <Table.Td ta="right">
                                    {row.openIssueCount > 0 ? (
                                      <Badge size="sm" color="orange" variant="light">
                                        {row.openIssueCount}
                                      </Badge>
                                    ) : (
                                      0
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                              );
                            })}
                          </Table.Tbody>
                        </Table>
                      </Table.ScrollContainer>
                    </Box>
                  )}
                </Card>

                {selectedRow ? (
                  <Card
                    withBorder
                    radius={CARD_RADIUS}
                    p="md"
                    className="location-health-detail"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      minWidth: 0,
                      position: 'sticky',
                      top: 16,
                      alignSelf: 'start',
                      maxHeight: 'calc(100vh - 120px)',
                    }}
                  >
                    <Group justify="space-between" mb="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Stack gap={2} style={{ minWidth: 0 }}>
                        <Title order={4} lineClamp={1}>
                          {selectedRow.name}
                        </Title>
                        <Text size="xs" c="dimmed">
                          Rolled-up leaf tasks (this block and descendants)
                        </Text>
                      </Stack>
                      <UnstyledButton onClick={() => setSelectedLocationId(null)}>
                        <Text size="sm" c="dimmed">
                          Clear
                        </Text>
                      </UnstyledButton>
                    </Group>
                    <Stack gap={6} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                      {selectedRow.tasks.map((t) => (
                        <Group
                          key={t.id}
                          justify="space-between"
                          wrap="nowrap"
                          p="xs"
                          style={{
                            borderRadius: 8,
                            border: '1px solid var(--mantine-color-default-border)',
                            background: 'var(--mantine-color-default)',
                          }}
                        >
                          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                            <Text size="sm" fw={500} truncate>
                              {t.name}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                              Finish {formatDate(t.finish)} · {t.progress}%
                            </Text>
                          </Stack>
                          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                            {t.overdue ? (
                              <Badge size="xs" color="red" variant="light">
                                Overdue
                              </Badge>
                            ) : null}
                            {t.openIssueCount > 0 ? (
                              <Badge size="xs" color="orange" variant="light">
                                {t.openIssueCount} issue{t.openIssueCount === 1 ? '' : 's'}
                              </Badge>
                            ) : null}
                          </Group>
                        </Group>
                      ))}
                    </Stack>
                  </Card>
                ) : null}
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export default LocationScheduleHealthPage;
